import { useEffect, useMemo, useState } from "react";
import { FiSearch, FiDownload, FiEye } from "react-icons/fi";
import { message } from "antd";
import LabOrderService from "../../../../Services/LabOrderService";

function parseVitalDate(dt) {
  if (!dt) return null;
  const d = new Date(dt.replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(dt) {
  const d = parseVitalDate(dt);
  if (!d) return "-";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function deriveStatus(item) {
  return item?.isLabReportUploaded === "YES" || item?.resultString ? "Completed" : "Pending";
}

export default function Labs({ patientId }) {
  const [labReports, setLabReports] = useState([]);
  const [labReportLoad, setLabReportLoad] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const patientLabReports = async () => {
      if (!patientId) return;
      setLabReportLoad(true);
      const payload = {
        patientId,
        pageNo: 1,
        status: 1,
      };
      try {
        const response = await LabOrderService.getLabReportsData(payload);
        setLabReports(response?.data || []);
      } catch (e) {
        console.log(e);
        setLabReports([]);
      } finally {
        setLabReportLoad(false);
      }
    };
    patientLabReports();
  }, [patientId]);

  const handleViewLabReports = (report) => {
    const file = report?.resultDocumentName;
    if (!file) {
      message.error("No file found!!");
      return;
    }
    if (file?.startsWith("http") || file?.startsWith("https")) {
      window.open(file, "_blank");
    } else {
      window.open(import.meta.env.VITE_APP_IMAGE_URL + file, "_blank");
    }
  };

  const handleDownloadLabReport = (report) => {
    const file = report?.resultDocumentName;
    if (!file) {
      message.error("No file found!!");
      return;
    }
    const url = file?.startsWith("http") || file?.startsWith("https")
      ? file
      : import.meta.env.VITE_APP_IMAGE_URL + file;

    const link = document.createElement("a");
    link.href = url;
    link.download = file;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sortedLabReports = useMemo(
    () =>
      [...labReports].sort((a, b) => {
        const dateA = a.uploadedDateTime ? parseVitalDate(a.uploadedDateTime)?.getTime() || 0 : 0;
        const dateB = b.uploadedDateTime ? parseVitalDate(b.uploadedDateTime)?.getTime() || 0 : 0;
        if (dateA !== dateB) return dateB - dateA;
        return (b.id || 0) - (a.id || 0);
      }),
    [labReports]
  );

  const filteredLabReports = useMemo(() => {
    return sortedLabReports.filter((item) => {
      const matchesSearch = searchTerm
        ? (item.labTestName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.labTestOrderId || "").toLowerCase().includes(searchTerm.toLowerCase())
        : true;

      const matchesDate = dateFilter
        ? parseVitalDate(item.uploadedDateTime)
          ? new Date(item.uploadedDateTime.replace(" ", "T")).toISOString().slice(0, 10) === dateFilter
          : false
        : true;

      return matchesSearch && matchesDate;
    });
  }, [sortedLabReports, searchTerm, dateFilter]);

  return (
    <div className="rounded-lg border bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b p-4">
        <h2 className="text-sm font-semibold text-[#3b3f97]">Lab Reports</h2>
        <div className="flex gap-3">
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="h-8 rounded-md border border-[#e5e7eb] bg-white px-4 text-[13px] font-semibold text-[#111827] outline-none focus:border-[#5b5bd6]"
          />
          <div className="relative">
            <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="rounded-lg border py-2 pl-9 pr-3 text-xs outline-none"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="p-3 text-left">Report ID</th>
              <th className="p-3 text-left">Test</th>
              <th className="p-3 text-left">Sample Date</th>
              <th className="p-3 text-left">Report Date</th>
              <th className="p-3 text-left">Doctor</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {labReportLoad ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-400">
                  Loading lab reports...
                </td>
              </tr>
            ) : filteredLabReports.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-400">
                  No lab reports found.
                </td>
              </tr>
            ) : (
              filteredLabReports.map((item) => {
                const status = deriveStatus(item);
                return (
                  <tr key={item.id} className="border-t hover:bg-gray-50">
                    <td className="p-3">{item.labTestOrderId || "-"}</td>
                    <td className="p-3 font-medium">{item.labTestName || "-"}</td>
                    <td className="p-3">{item.sampleDate ? formatDate(item.sampleDate) : "-"}</td>
                    <td className="p-3">{item.uploadedDateTime ? formatDate(item.uploadedDateTime) : "-"}</td>
                    <td className="p-3">{item.doctor || "-"}</td>

                    <td className="p-3">
                      <span
                        className={`rounded px-2 py-1 text-[10px] ${
                          status === "Completed"
                            ? "bg-green-100 text-green-700"
                            : "bg-orange-100 text-orange-700"
                        }`}
                      >
                        {status}
                      </span>
                    </td>

                    <td className="p-3">
                      <div className="flex gap-3 justify-center">
                        <button
                          onClick={() => handleViewLabReports(item)}
                          className="rounded border p-2 text-blue-600 hover:bg-blue-50"
                        >
                          <FiEye size={14} />
                        </button>
                        <button
                          onClick={() => handleDownloadLabReport(item)}
                          className="rounded border p-2 text-blue-600 hover:bg-blue-50"
                        >
                          <FiDownload size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
